// src/pages/caixas/PesqResumoCaixaPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaSortDown, FaSortUp } from 'react-icons/fa';
import { fetchData } from '../../services/Api.js';
import { useMessage } from '../../contexts/geral/MessageContext.js';
import { formatCurrency } from '../../utils/numberUtils.js';
import { formatDate, formatTime } from '../../utils/dateTimeUtils.js';
import ModalContainer from '../../components/modalForm/ModalContainer.js';
import Input from '../../components/input/Input.js';
import PageContainer from '../../components/geral/PageContainer.js';
import PageHeader from '../../components/geral/PageHeader.js';
import PageFooter from '../../components/geral/PageFooter.js';
import PageContent from '../../components/geral/PageContent.js';
import ModalFooter from '../../components/modalForm/ModalFooter.js';
import ModalBody from '../../components/modalForm/ModalBody.js';
import ModalHeader from '../../components/modalForm/ModalHeader.js';
import { usePrinterCaixa } from '../../contexts/geral/PrinterCaixaContext.js';
import { getDescricaoTipoMov } from '../../models/caixas/TipoMovCaixa.js';

const PesqResumoCaixaPage = () => {
    const { showMessage } = useMessage();
    const [periodoInicial, setPeriodoInicial] = useState('');
    const [periodoFinal, setPeriodoFinal] = useState('');
    const [resumos, setResumos] = useState([]);
    const [selecionados, setSelecionados] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [resumoDetalhado, setResumoDetalhado] = useState([]);
    const [movVendas, setMovVendas] = useState([]);
    const [movimentosObservados, setMovimentosObservados] = useState([]);
    const [ordenacao, setOrdenacao] = useState({ campo: null, direcao: 'asc' });
    const { printResumoCaixa } = usePrinterCaixa();

    useEffect(() => {
        const dataAtual = new Date();
        const primeiroDiaMes = new Date(dataAtual.getFullYear(), dataAtual.getMonth() - 1, 1);
        const dataAtualFormatada = dataAtual.toISOString().split('T')[0];
        const primeiroDiaMesFormatado = primeiroDiaMes.toISOString().split('T')[0];

        setPeriodoInicial(primeiroDiaMesFormatado);
        setPeriodoFinal(dataAtualFormatada);
    }, []);

    useEffect(() => {
        if (periodoInicial && periodoFinal) {
            buscarResumos();
        }
    }, [periodoInicial, periodoFinal]);

    const buscarResumos = useCallback(async () => {
        try {
            const result = await fetchData('/CaixaResumo/GetCaixaResumosByFilter');
            setResumos(result.reverse());
        } catch (error) {
            showMessage(`Erro ao buscar resumos: ${error}`, 'danger');
        }
    }, [showMessage, setResumos]);

    const calcularTotal = () => {
        return resumos.reduce((acc, resumo) => acc + resumo.valorFechamento, 0);
    };

    const resumosOrdenados = useMemo(() => {
        const { campo, direcao } = ordenacao;

        if (!campo) {
            return resumos;
        }

        const multiplicador = direcao === 'asc' ? 1 : -1;

        return [...resumos].sort((a, b) => {
            const valorA = campo === 'caixa'
                ? a.caixa?.descricao
                : campo === 'usuarioAber'
                    ? a.usuarioAber?.nome
                    : a?.[campo];
            const valorB = campo === 'caixa'
                ? b.caixa?.descricao
                : campo === 'usuarioAber'
                    ? b.usuarioAber?.nome
                    : b?.[campo];

            if (['id', 'valorAbertura', 'valorEntrada', 'valorSaida', 'valorFechamento'].includes(campo)) {
                return (Number(valorA || 0) - Number(valorB || 0)) * multiplicador;
            }

            if (campo === 'abertura' || campo === 'fechamento') {
                return (new Date(valorA || 0) - new Date(valorB || 0)) * multiplicador;
            }

            return String(valorA || '').localeCompare(String(valorB || ''), 'pt-BR', {
                sensitivity: 'base',
            }) * multiplicador;
        });
    }, [resumos, ordenacao]);

    const alterarOrdenacao = (campo) => {
        setOrdenacao((atual) => ({
            campo,
            direcao: atual.campo === campo
                ? (atual.direcao === 'asc' ? 'desc' : 'asc')
                : 'asc',
        }));
    };

    const renderOrdenadorColuna = (campo, titulo, style = {}) => {
        const ativa = ordenacao.campo === campo;

        return (
            <button
                type="button"
                onClick={() => alterarOrdenacao(campo)}
                style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    fontWeight: 'bold',
                    color: 'inherit',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: style.textAlign || 'left',
                }}
            >
                {titulo}
                {ativa && (ordenacao.direcao === 'asc' ? <FaSortUp /> : <FaSortDown />)}
            </button>
        );
    };

    const selecionarResumo = (id) => {
        setSelecionados([id]);
    };

    const handleImprimir = () => {
        selecionados.forEach((caixaResumo) => {
            printResumoCaixa(caixaResumo);
        });
    };

    const handleVerDetalhes = async (id) => {
        try {
            const [resumo, movimentosPag, movimentosCaixa] = await Promise.all([
                fetchData('/CaixaResumo/GetCaixaResumoById', { id }),
                fetchData('/CaixaResumo/GetMovimentacoesPag', { caixaResumoId: id }),
                fetchData('/CaixaMov', { caixaResumoId: id }),
            ]);
            setMovVendas(movimentosPag);
            setMovimentosObservados(movimentosCaixa
                .filter((movimento) => movimento.observacao?.trim())
                .sort((a, b) => new Date(a.dataHoraMov) - new Date(b.dataHoraMov)));
            setResumoDetalhado(resumo);
            setModalOpen(true);
        } catch (error) {
            showMessage(`Erro ao buscar detalhes do resumo: ${error}`, 'danger');
        }
    };

    return (
        <PageContainer>
            <h2 className="page-title">Resumo de Caixa</h2>
            <PageHeader>
                <div className="group-div">
                    <Input
                        label="Período Inicial"
                        onChange={(e) => setPeriodoInicial(e.target.value)}
                        type="date"
                        size={150}
                        value={periodoInicial}
                    />
                    <Input
                        label="Período Final"
                        onChange={(e) => setPeriodoFinal(e.target.value)}
                        type="date"
                        size={150}
                        value={periodoFinal}
                    />
                </div>

                <button
                    style={{ marginRight: '5px' }}
                    type="button"
                    className="btn btn-search"
                    onClick={buscarResumos}
                >
                    Buscar
                </button>
            </PageHeader>

            <PageContent>
                <table className="page-content-table">
                    <thead>
                        <tr>
                            <th></th>
                            <th style={{ minWidth: '50px' }}>{renderOrdenadorColuna('id', 'ID')}</th>
                            <th style={{ minWidth: '70px' }}>{renderOrdenadorColuna('caixa', 'Caixa')}</th>
                            <th style={{ minWidth: '100px' }}>
                                {renderOrdenadorColuna('usuarioAber', 'Usuario Abertura')}
                            </th>
                            <th>{renderOrdenadorColuna('abertura', 'Data Abertura')}</th>
                            <th style={{ textAlign: 'right' }}>
                                {renderOrdenadorColuna('valorAbertura', 'Valor Abertura', { textAlign: 'right' })}
                            </th>
                            <th style={{ textAlign: 'right' }}>
                                {renderOrdenadorColuna('valorEntrada', 'Entradas', { textAlign: 'right' })}
                            </th>
                            <th style={{ textAlign: 'right' }}>
                                {renderOrdenadorColuna('valorSaida', 'Saidas', { textAlign: 'right' })}
                            </th>
                            <th>{renderOrdenadorColuna('fechamento', 'Data Fechamento')}</th>
                            <th style={{ minWidth: '100px', textAlign: 'right' }}>
                                {renderOrdenadorColuna('valorFechamento', 'Valor Fec.', { textAlign: 'right' })}
                            </th>
                            <th>Acoes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {resumosOrdenados.map((resumo) => (
                            <tr key={resumo.id}>
                                <td style={{ textAlign: 'center' }}>
                                    <input
                                        type="radio"
                                        name="resumoCaixaSelecionado"
                                        checked={selecionados.includes(resumo.id)}
                                        onChange={() => selecionarResumo(resumo.id)}
                                    />
                                </td>
                                <td>{resumo.id}</td>
                                <td>{resumo.caixa.descricao}</td>
                                <td>{resumo.usuarioAber.nome}</td>
                                <td>{formatDate(resumo.abertura)} {formatTime(resumo.abertura)}</td>
                                <td style={{ textAlign: 'right' }}>{formatCurrency(resumo.valorAbertura)}</td>
                                <td style={{ textAlign: 'right' }}>{formatCurrency(resumo.valorEntrada)}</td>
                                <td style={{ textAlign: 'right' }}>{formatCurrency(resumo.valorSaida)}</td>
                                <td>
                                    {resumo.fechamento
                                        ? `${formatDate(resumo.fechamento)} ${formatTime(resumo.fechamento)}`
                                        : '-'}
                                </td>
                                <td style={{ textAlign: 'right' }}>{formatCurrency(resumo.valorFechamento)}</td>
                                <td style={{ width: '110px' }}>
                                    <button className="btn btn-table" onClick={() => handleVerDetalhes(resumo.id)}>
                                        Ver Detalhes
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </PageContent>

            <PageFooter>
                <div className="botoes-footer">
                    <button onClick={handleImprimir} className="btn btn-secondary btn-footer">
                        Imprimir 2ª Via
                    </button>
                </div>
                <div className="totais-footer">
                    <strong>Total: </strong> {formatCurrency(calcularTotal())}
                </div>
            </PageFooter>

            <ModalContainer largura="800px" show={modalOpen}>
                <ModalHeader>
                    <h5 className="modal-title">{`Detalhamento do Resumo Nº ${resumoDetalhado.id}`}</h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setModalOpen(false)} />
                </ModalHeader>
                {resumoDetalhado && (
                    <ModalBody>
                        <div style={{ padding: '0px', fontFamily: 'Arial, sans-serif' }}>
                            <div style={{ marginTop: '-5px', marginBottom: '10px', borderBottom: '2px solid #ddd', paddingBottom: '8px' }}>
                                <h5 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px', color: '#4C9BFB' }}>
                                    Fechamento de Caixa
                                </h5>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                    <div><strong>Caixa:</strong> {resumoDetalhado.caixa?.descricao}</div>
                                    <div><strong>Usuário Fechamento:</strong> {resumoDetalhado.usuarioAber?.nome}</div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                    <div><strong>Data Abertura:</strong> {formatDate(resumoDetalhado.abertura)} {formatTime(resumoDetalhado.abertura)}</div>
                                    <div><strong>Data Fechamento:</strong> {formatDate(resumoDetalhado.fechamento)} {formatTime(resumoDetalhado.fechamento)}</div>
                                </div>
                            </div>

                            <div style={{ marginBottom: '10px', borderBottom: '2px solid #ddd', paddingBottom: '8px' }}>
                                <h5 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px', color: '#4C9BFB' }}>
                                    Movimentações de Venda
                                </h5>
                                {movVendas.map((mov, index) => (
                                    <div key={index} style={{ marginBottom: '4px' }}>
                                        <strong>{mov.formaPag}:</strong> {formatCurrency(mov.vPag)}
                                    </div>
                                ))}
                                <div style={{ marginTop: '4px', fontWeight: 'bold', color: '#28A745' }}>
                                    <strong>Total Vendas:</strong> {formatCurrency(movVendas.reduce((acc, mov) => acc + mov.vPag, 0))}
                                </div>
                            </div>

                            {movimentosObservados.length > 0 && (
                                <div style={{ marginBottom: '10px', borderBottom: '2px solid #ddd', paddingBottom: '8px' }}>
                                    <h5 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px', color: '#4C9BFB' }}>
                                        Observações das Movimentações
                                    </h5>
                                    {movimentosObservados.map((movimento) => (
                                        <div key={movimento.id} style={{ marginBottom: '8px' }}>
                                            <div>
                                                <strong>{getDescricaoTipoMov(movimento.tipoMovCaixa)}</strong>
                                                {' - '}{formatDate(movimento.dataHoraMov)} {formatTime(movimento.dataHoraMov)}
                                                {' - '}{formatCurrency(Math.abs(movimento.vRec ?? movimento.vPag ?? 0))}
                                            </div>
                                            <div style={{ whiteSpace: 'pre-wrap' }}>{movimento.observacao}</div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div style={{ marginBottom: '10px' }}>
                                <h5 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px', color: '#4C9BFB' }}>
                                    Resumo do Caixa
                                </h5>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                    <div><strong>Valor Abertura:</strong> {formatCurrency(resumoDetalhado.valorAbertura)}</div>
                                    <div><strong>Total Entradas:</strong> {formatCurrency(resumoDetalhado.valorEntrada)}</div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                    <div><strong>Total Saídas:</strong> {formatCurrency(resumoDetalhado.valorSaida)}</div>
                                    <div><strong>Valor Fechamento:</strong> {formatCurrency(resumoDetalhado.valorFechamento)}</div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                    <div>
                                        <strong>Saldo Final:</strong>{' '}
                                        <span style={{ color: '#28A745' }}>
                                            {formatCurrency((resumoDetalhado?.valorAbertura + resumoDetalhado?.valorEntrada) - resumoDetalhado?.valorSaida)}
                                        </span>
                                    </div>
                                    {((resumoDetalhado?.valorAbertura + resumoDetalhado?.valorEntrada) - resumoDetalhado?.valorSaida) !== resumoDetalhado?.valorFechamento && (
                                        <div style={{ marginBottom: '6px', color: '#DC3545' }}>
                                            <strong>Diferença:</strong>{' '}
                                            {formatCurrency(
                                                resumoDetalhado?.valorFechamento
                                                - (resumoDetalhado?.valorAbertura + resumoDetalhado?.valorEntrada - resumoDetalhado?.valorSaida),
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </ModalBody>
                )}
                <ModalFooter>
                    <button type="button" className="btn btn-primary" onClick={() => setModalOpen(false)}>
                        Fechar
                    </button>
                </ModalFooter>
            </ModalContainer>
        </PageContainer>
    );
};

export default PesqResumoCaixaPage;
